import {
    FulfillmentDistributionCenter,
    FulfillmentDistributionCenterInventory,
} from 'common/types';
import { SHIPPING_RADIUS_CONFIG } from 'common/types/distributionCenterAndInventory';
import { PRODUCT_CATEGORY } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { chain, sumBy } from 'lodash';

const getConfiguredRadius = (
    distributionCenter: FulfillmentDistributionCenter,
    key: string
): number | undefined => {
    const config = distributionCenter.configuration?.find(
        con => con.type === 'FULFILLMENT' && con.key === key
    );
    return config ? Number(config.value) : undefined;
};

/**
 * Returns the shipping radius that applies to the order being fulfilled.
 *
 * Equipment and parts now have separate radii, so the radius depends on what
 * the order contains: if it has equipment we must respect the equipment radius,
 * and likewise for parts. When an order spans both categories the distribution
 * center has to be able to deliver each one, so the smaller of the applicable
 * radii governs whether it is in service. When the order's categories are not
 * known we fall back to the smallest configured radius, matching the backend
 * which requires every category radius to be satisfied (a DC is excluded if any
 * one falls outside).
 */
export function getDistributionRadius(
    distributionCenter: FulfillmentDistributionCenter,
    inventory: Pick<
        FulfillmentDistributionCenterInventory,
        'productCategoryId'
    >[] = []
) {
    if (!distributionCenter?.configuration) {
        return 0;
    }

    const equipmentRadius = getConfiguredRadius(
        distributionCenter,
        SHIPPING_RADIUS_CONFIG.Equipment
    );
    const partsRadius = getConfiguredRadius(
        distributionCenter,
        SHIPPING_RADIUS_CONFIG.Part
    );

    const hasEquipment = !!inventory?.some(
        item => item.productCategoryId === PRODUCT_CATEGORY.EQUIPMENT
    );
    const hasParts = !!inventory?.some(
        item => item.productCategoryId === PRODUCT_CATEGORY.PART
    );

    const applicableRadii: number[] = [];
    if (hasEquipment) applicableRadii.push(equipmentRadius ?? 0);
    if (hasParts) applicableRadii.push(partsRadius ?? 0);

    if (applicableRadii.length > 0) {
        return Math.min(...applicableRadii);
    }

    const configuredRadii = [equipmentRadius, partsRadius].filter(
        (radius): radius is number => radius !== undefined
    );
    return configuredRadii.length > 0 ? Math.min(...configuredRadii) : 0;
}

export const isInStock = (
    distributionCenter: FulfillmentDistributionCenter,
    inventory: { id: number; qty?: number }[]
) => {
    if (distributionCenter.inventory && distributionCenter.inventory.length) {
        const _inventory = chain(inventory)
            .groupBy('id')
            .map((group, id) => ({ id, qty: sumBy(group, 'qty') }))
            .value();

        for (let i = 0; i < _inventory.length; i++) {
            if (
                !distributionCenter.inventory.some(
                    item =>
                        item.productId.toString() === _inventory[i].id &&
                        item.quantityAvailable >= _inventory[i].qty
                )
            )
                return false;
        }

        return true;
    }

    return false;
};

export const mapisPickupOrIsDelivery = (
    vendorDistributionCenters: FulfillmentDistributionCenter[]
) => {
    return vendorDistributionCenters.map(vdc => {
        const configuration = vdc?.configuration;
        if (configuration) {
            const isPickup = !!vdc.configuration.find(
                fulfillmentConfig =>
                    fulfillmentConfig.key === 'FULFILLMENT_TYPE_ID' &&
                    fulfillmentConfig.value === 'PICK_UP'
            );
            const isDelivery = !!vdc.configuration.find(
                fulfillmentConfig =>
                    fulfillmentConfig.key === 'FULFILLMENT_TYPE_ID' &&
                    fulfillmentConfig.value === 'DELIVERY'
            );

            const isPickupOnly = isPickup && !isDelivery;

            return {
                ...vdc,
                isPickup,
                isDelivery,
                isPickupOnly,
            };
        }
        return vdc;
    });
};

export const isPickupOrDeliveryMessage = (
    vdc: FulfillmentDistributionCenter
) => {
    if (vdc?.isDelivery && vdc?.isPickup) {
        return 'Pickup/Delivery';
    }
    if (vdc?.isPickup) {
        return 'Pickup Only';
    }
    if (vdc?.isDelivery) {
        return 'Delivery Only';
    }
    return '';
};

export const shouldRecheckDeliveryDistributionCenter = (
    distributionCenter: FulfillmentDistributionCenter | null | undefined
) => !!distributionCenter?.preferredFulfillment;

export const cartKey = (inventory: FulfillmentDistributionCenterInventory[]) =>
    inventory
        .map(product => `${product.id}:${product.qty}`)
        .sort()
        .join(',');
